// The Social posts block's link reading, apart from the components so
// the harness can run it in node. See SiteSocialFeed.vue.
//
// A manager pastes whatever the share button gave them. Each platform
// has a handful of URL shapes for one post, and a link this cannot
// read is reported in the editor rather than rendered as an empty box.

export const MAX_SOCIAL_POSTS = 12;

export const SOCIAL_PLATFORM_LABELS = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    x: 'X',
};

export const SOCIAL_FEED_DEFAULTS = {
    source: 'posts',
    posts: [],
    youtube_channel: '',
    count: 6,
    columns: 3,
};

function asUrl(raw) {
    const s = String(raw || '').trim();
    if (!s) return null;
    try {
        const u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
        return u.protocol === 'https:' || u.protocol === 'http:' ? u : null;
    } catch {
        return null;
    }
}

const host = (u) => u.hostname.toLowerCase().replace(/^(www\.|m\.|mobile\.)/, '');

/** @returns {{platform: string, id: string, url: string}|null} */
export function parseSocialUrl(raw) {
    const u = asUrl(raw);
    if (!u) return null;
    const h = host(u);
    const path = u.pathname;
    let m;

    if (h === 'instagram.com') {
        m = path.match(/^\/(?:[A-Za-z0-9._]+\/)?(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/);
        if (m) {
            const kind = m[1] === 'reels' ? 'reel' : m[1];
            return { platform: 'instagram', id: m[2], url: `https://www.instagram.com/${kind}/${m[2]}/` };
        }
        return null;
    }
    if (h === 'youtube.com' || h === 'youtu.be' || h === 'youtube-nocookie.com') {
        let id = null;
        if (h === 'youtu.be') id = path.slice(1).split('/')[0];
        else if (path === '/watch') id = u.searchParams.get('v');
        else if ((m = path.match(/^\/(?:shorts|live|embed)\/([^/?#]+)/))) id = m[1];
        return id && /^[A-Za-z0-9_-]{11}$/.test(id)
            ? { platform: 'youtube', id, url: `https://www.youtube.com/watch?v=${id}`, vertical: /^\/shorts\//.test(path) }
            : null;
    }
    if (h === 'tiktok.com') {
        m = path.match(/^\/@[^/]+\/(?:video|photo)\/(\d{6,25})/);
        return m ? { platform: 'tiktok', id: m[1], url: `https://www.tiktok.com${path}` } : null;
    }
    if (h === 'x.com' || h === 'twitter.com') {
        m = path.match(/^\/([A-Za-z0-9_]{1,15})\/status(?:es)?\/(\d{5,25})/);
        return m ? { platform: 'x', id: m[2], url: `https://twitter.com/${m[1]}/status/${m[2]}` } : null;
    }
    if (h === 'facebook.com' || h === 'fb.watch') {
        // Facebook's shapes are many and change; a post, a video, a
        // reel, a photo, a permalink or a share link. The embed asks
        // Facebook about the URL as given, so this only has to know it
        // is one post rather than a page or a profile.
        const isPost = h === 'fb.watch'
            || /\/(posts|videos|reel|photos|permalink\.php|photo\.php|story\.php|watch)\b/.test(path)
            || /^\/share\/[prv]\//.test(path)
            || u.searchParams.has('story_fbid') || u.searchParams.has('fbid') || u.searchParams.has('v');
        if (!isPost) return null;
        const clean = `https://www.facebook.com${path}${u.search}`;
        return { platform: 'facebook', id: clean, url: h === 'fb.watch' ? u.href : clean };
    }
    return null;
}

/** The posts a block shows: readable links only, no repeats, capped. */
export function socialPostItems(config) {
    const seen = new Set();
    const out = [];
    for (const row of Array.isArray(config?.posts) ? config.posts : []) {
        const p = parseSocialUrl(row?.url);
        if (!p) continue;
        const key = `${p.platform}:${p.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(p);
        if (out.length >= MAX_SOCIAL_POSTS) break;
    }
    return out;
}

/**
 * Whether the block has anything to show a visitor before it loads:
 * pasted posts, or a YouTube channel to read. A channel whose feed then
 * fails or is empty is reported by SiteSocialFeed's `empty` event.
 */
export function socialFeedHasContent(config) {
    if (socialFeedSource(config) === 'youtube') return String(config?.youtube_channel ?? '').trim() !== '';
    return socialPostItems(config).length > 0;
}

export function socialFeedSource(config) {
    return config?.source === 'youtube' ? 'youtube' : 'posts';
}

export function socialFeedColumns(config) {
    const n = Number(config?.columns);
    return n === 2 || n === 4 ? n : 3;
}

export function youtubeFeedUrl({ orgSlug, channel, count = 6, canonical, hostPrefix }) {
    const c = String(channel || '').trim();
    if (!c) return null;
    let base = '';
    if (canonical) {
        if (!orgSlug) return null;
        base = `/site/${encodeURIComponent(orgSlug)}`;
    } else if (typeof hostPrefix === 'string' && /^\/[a-z0-9-]+$/.test(hostPrefix)) {
        // The public host, go.trailsiq.com/{slug} — sitePrefix().
        base = hostPrefix;
    }
    const n = Math.max(1, Math.min(12, Number(count) || 6));
    return `${base}/youtube.json?channel=${encodeURIComponent(c)}&count=${n}`;
}
